import { SHIPMENT_EVENT_NAMES } from '@freightapi/core/events/catalog'
import type { ShipmentEvent } from '@freightapi/core/events/event'
import {
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Field,
  formatDateTime,
} from '@freightapi/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type FormEvent, useCallback, useState } from 'react'
import { ResourceView } from '../components/ResourceView'
import { type EventFilters, fetchEvents } from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useMode } from '../lib/mode'
import { useResource } from '../lib/resource'

/**
 * The organisation's event feed (M3-06): every event of every shipment, newest first.
 *
 * It reads `/v1/console/events`, the session alias of the M1-05 feed — the same rows, the same
 * order and the same cursor an integration polls. That matters more than it looks: an answer given
 * from this screen ("we did send that at 10:12") is an answer about the API, not about a separate
 * console view of it.
 *
 * Every row links to its shipment, because "what happened" is almost never the last question.
 */
export const Route = createFileRoute('/_app/events')({ component: Events })

type EventType = ShipmentEvent['type']

function Events() {
  const { t, locale, sign } = useConsoleI18n()
  const { mode } = useMode()
  const [draft, setDraft] = useState<EventType | ''>('')
  const [applied, setApplied] = useState<EventType | ''>('')
  const [trail, setTrail] = useState<readonly string[]>([])
  const [trailMode, setTrailMode] = useState(mode)
  const cursor = trail.at(-1)

  // Another mode is another feed, so a cursor from this one means nothing there.
  if (trailMode !== mode) {
    setTrailMode(mode)
    setTrail([])
  }

  const load = useCallback(
    (signal: AbortSignal) =>
      fetchEvents(
        mode,
        { ...toFilters(applied), ...(cursor === undefined ? {} : { cursor }) },
        signal,
      ),
    [mode, applied, cursor],
  )
  const { state } = useResource(load)

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setTrail([])
    setApplied(draft)
  }

  const columns: readonly DataTableColumn<ShipmentEvent>[] = [
    {
      key: 'type',
      header: t('events.column.type'),
      cell: (row) => (
        <>
          <span className="block font-medium text-ink">{sign(`event.${row.type}`)}</span>
          <span className="block font-mono text-caption text-muted">{row.type}</span>
        </>
      ),
    },
    {
      key: 'shipment',
      header: t('events.column.shipment'),
      cell: (row) => (
        <Link
          to="/shipments/$id"
          params={{ id: row.shipment_id }}
          className="break-all font-mono text-caption text-brand-700 underline"
        >
          {row.shipment_id}
        </Link>
      ),
    },
    {
      key: 'actor',
      header: t('events.column.actor'),
      cell: (row) => (
        <span className="text-caption text-muted">
          {row.actor.label ?? sign(`actor.${row.actor.type}`)}
        </span>
      ),
    },
    {
      key: 'seq',
      header: t('events.column.seq'),
      numeric: true,
      cell: (row) => row.seq,
    },
    {
      key: 'moment',
      header: t('events.column.moment'),
      cell: (row) => (
        <span className="text-caption text-muted">{formatDateTime(row.occurred_at, locale)}</span>
      ),
    },
  ]

  return (
    <section className="flex flex-col gap-6">
      <h1 className="font-semibold text-display text-ink">{t('nav.events.label')}</h1>
      <p className="text-body text-muted">{t('events.intro')}</p>

      <form
        className="flex flex-wrap items-end gap-4 rounded-card border border-line p-4"
        onSubmit={onSubmit}
      >
        <Field label={t('events.filter.type')}>
          {({ id }) => (
            <select
              id={id}
              value={draft}
              onChange={(event) => setDraft(event.target.value as EventType | '')}
              className="min-h-touch rounded-card border border-line bg-surface px-2 text-body text-ink"
            >
              <option value="">{t('events.filter.type.any')}</option>
              {SHIPMENT_EVENT_NAMES.map((name) => (
                <option key={name} value={name}>
                  {sign(`event.${name}`)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <div className="flex gap-2">
          <Button type="submit">{t('filter.apply')}</Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft('')
              setApplied('')
              setTrail([])
            }}
          >
            {t('filter.reset')}
          </Button>
        </div>
      </form>

      <ResourceView state={state}>
        {(page) => (
          <>
            <DataTable
              caption={t('events.table.caption')}
              columns={columns}
              rows={page.data}
              rowKey={(row) => row.id}
              empty={
                <EmptyState title={t('events.empty.title')} description={t('events.empty.body')} />
              }
            />
            <div className="flex gap-2">
              <Button
                variant="ghost"
                disabled={trail.length === 0}
                onClick={() => setTrail((followed) => followed.slice(0, -1))}
              >
                {t('page.previous')}
              </Button>
              <Button
                variant="ghost"
                disabled={page.next_cursor === null}
                onClick={() =>
                  setTrail((followed) =>
                    page.next_cursor === null ? followed : [...followed, page.next_cursor],
                  )
                }
              >
                {t('page.next')}
              </Button>
            </div>
          </>
        )}
      </ResourceView>
    </section>
  )
}

function toFilters(type: EventType | ''): EventFilters {
  return type === '' ? {} : { type }
}
